import {
    FormControl,
    FormHelperText,
    Input,
    InputAdornment,
    InputLabel,
    MenuItem,
    MenuList,
    Paper,
    Popper,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import Downshift from "downshift";
import PropTypes from "prop-types";
import { useRef, useState } from "react";
import { emptyValues } from "../../../app/js/utils/constants";
import ErrorIcon from "./ErrorIcon";

const StyledFormHelperText = styled(FormHelperText)(({ required }) => ({
    fontStyle: "italic",
    margin: "0px",

    ...(required && {
        color: "dimgrey",
    }),
}));

const StyledFormControl = styled(FormControl)(() => ({
    marginBottom: "22px",
    width: "100%",
}));

// When reserving label/helper space, the reserved helper line supplies the row's
// vertical spacing, so the FormControl drops its own bottom margin. This keeps the
// reserve behavior self-contained and leaves extraFormControlStyles as a clean
// escape hatch layered on top so extraFormControlStyles and reserveLabelSpace do not silently fight over margin.
const StyledReservedFormControl = styled(StyledFormControl)({
    marginBottom: 0,
});

const StyledInput = styled(Input)(({ theme }) => ({
    borderRadius: 4,
    border: "1px solid #E0E0E0",
    fontSize: 14,
    padding: "4px",
    transition: theme.transitions.create(["border-color", "box-shadow"]),
    "&:focus": {
        borderColor: "#80bdff",
        boxShadow: "0 0 0 0.2rem rgba(0,123,255,.25)",
    },
}));

const StyledMenuItemTitle = styled("span")({
    fontSize: "14px",
    margin: 0,
    padding: 0,
});

const StyledMenuItemSubtext = styled("span")({
    fontSize: "12px",
    margin: 0,
    padding: 0,
});

const StyledMenuItem = styled("div")({
    width: "100%",
    lineHeight: "1em",
});

const StyledResultType = styled("div")({
    float: "right",
    textTransform: "uppercase",
    fontSize: "13px",
    fontWeight: 600,
    color: "#9E9E9E",
});

const StyledPaper = styled(Paper)(() => ({
    maxHeight: "210px",
    overflowY: "auto",
    zIndex: 1300,
}));

const StyledContainer = styled("div")({
    flexGrow: 1,
    position: "relative",
});

const StyledFormLabel = styled(InputLabel)(({ theme }) => ({
    "&.MuiInputLabel-root": {
        "&.Mui-focused": {
            color: theme.palette.primary.main,
        },
    },
}));

const StyledReservedFormLabel = styled(StyledFormLabel)({
    minHeight: "0.5em",
});

const StyledReservedHelperText = styled(StyledFormHelperText)({
    minHeight: "0.5em",
});

function renderInput(inputProps) {
    const {
        endAdornment,
        hasError,
        InputProps,
        label,
        hideErrorIcon,
        ref,
        reserveLabelSpace,
        startAdornment,
        errorText,
        helperText,
        extraFormControlStyles,
        showHelperOnBlur,
    } = inputProps;

    const showHelperText = () => {
        if (helperText && !hasError && showHelperOnBlur) {
            return helperText;
        }
        return;
    };

    const showErrorIcon = hasError && !hideErrorIcon;
    const helperTextContext = showHelperText();

    const FormControlComponent = reserveLabelSpace ? StyledReservedFormControl : StyledFormControl;
    const LabelComponent = reserveLabelSpace ? StyledReservedFormLabel : StyledFormLabel;
    // With reserveLabelSpace the label slot always renders to hold its height.
    const showLabelSlot = reserveLabelSpace || label || showErrorIcon;

    const HelperTextComponent = reserveLabelSpace ? StyledReservedHelperText : StyledFormHelperText;
    const renderHelperText = () => {
        if (errorText && hasError) {
            return <HelperTextComponent hasError={true}>{errorText}</HelperTextComponent>;
        }
        if (helperTextContext) {
            return <HelperTextComponent>{helperText}</HelperTextComponent>;
        }
        if (reserveLabelSpace) {
            return <StyledReservedHelperText>&nbsp;</StyledReservedHelperText>;
        }
        return null;
    };

    return (
        <FormControlComponent style={{ ...extraFormControlStyles }}>
            {showLabelSlot && (
                <LabelComponent shrink>
                    {label}
                    {showErrorIcon && <ErrorIcon />}
                </LabelComponent>
            )}
            <StyledInput
                inputRef={ref}
                disableUnderline={true}
                autoComplete={"no"}
                style={hasError ? { borderColor: "red" } : {}}
                endAdornment={endAdornment && <InputAdornment position="end">{endAdornment}</InputAdornment>}
                startAdornment={startAdornment && <InputAdornment position="start">{startAdornment}</InputAdornment>}
                {...InputProps}
            />
            {renderHelperText()}
        </FormControlComponent>
    );
}

function renderSuggestion({
    suggestion,
    index,
    itemProps,
    highlightedIndex,
    selectedItem,
    key,
    display,
    displaySub,
    displayType,
}) {
    const isHighlighted = highlightedIndex === index;
    const isSelected = selectedItem ? (String(selectedItem[key]) || "").indexOf(suggestion[key]) > -1 : false;

    return (
        <MenuItem
            {...itemProps}
            key={key}
            selected={isHighlighted}
            component="div"
            className="waterloo"
            style={{
                fontWeight: isSelected ? 500 : 400,
                backgroundColor: isHighlighted ? "#F5F5F5" : "white",
                zIndex: "5000",
            }}
        >
            <StyledMenuItem>
                <StyledMenuItemTitle>{display(suggestion)}</StyledMenuItemTitle>
                <br />
                <StyledMenuItemSubtext>{displaySub && displaySub(suggestion)}</StyledMenuItemSubtext>
            </StyledMenuItem>
            <StyledResultType>{displayType && <div>{displayType(suggestion)} Display Type</div>}</StyledResultType>
        </MenuItem>
    );
}
renderSuggestion.propTypes = {
    highlightedIndex: PropTypes.number,
    index: PropTypes.number,
    itemProps: PropTypes.object,
    selectedItem: PropTypes.string,
    suggestion: PropTypes.shape({ label: PropTypes.string }).isRequired,
};

const Autocomplete = (props) => {
    const {
        id,
        label,
        hideErrorIcon,
        options,
        onSelect,
        disabled,
        placeholder,
        reserveLabelSpace,
        suggestions,
        itemToString,
        endAdornment,
        freeFormValue,
        searchFunction,
        startAdornment,
        initialInputValue,
        error,
        errorText,
        helperText,
        extraFormControlStyles,
        ariaLabel,
        onBlurValidate,
    } = props;

    const anchorRef = useRef(null);
    const isEmpty = (val) => emptyValues.has(val);
    const [showHelperOnBlur, setShowHelperOnBlur] = useState(isEmpty(initialInputValue));
    const [localError, setLocalError] = useState(false);

    const hasError = !!error || localError;

    const validateOnBlur = (currentValue) => {
        if (onBlurValidate) {
            setLocalError(!!onBlurValidate(id, currentValue));
        }
    };

    return (
        <div>
            <Downshift
                id={id}
                onSelect={(item) => {
                    setShowHelperOnBlur(isEmpty(itemToString(item)));
                    setLocalError(false);
                    onSelect(item);
                    validateOnBlur(itemToString(item));
                }}
                itemToString={itemToString}
                onInputValueChange={searchFunction}
                initialInputValue={initialInputValue}
            >
                {({
                    getRootProps,
                    isOpen,
                    inputValue,
                    getItemProps,
                    getMenuProps,
                    selectedItem,
                    getInputProps,
                    highlightedIndex,
                }) => {
                    return (
                        <StyledContainer {...getRootProps({}, { suppressRefError: true })} ref={anchorRef}>
                            {renderInput({
                                fullWidth: true,
                                extraFormControlStyles: extraFormControlStyles,
                                endAdornment: endAdornment,
                                startAdornment: startAdornment,
                                errorText: errorText,
                                helperText: helperText,
                                id: id,
                                label: label,
                                hideErrorIcon: hideErrorIcon,
                                reserveLabelSpace: reserveLabelSpace,
                                hasError: hasError,
                                showHelperOnBlur: showHelperOnBlur,
                                InputProps: getInputProps({
                                    placeholder: placeholder,
                                    value: isOpen ? inputValue : initialInputValue,
                                    disabled: disabled,
                                    ...(ariaLabel && { "aria-label": ariaLabel }),
                                    onBlur: () => {
                                        const selectedValue = isEmpty(selectedItem) ? "" : itemToString(selectedItem);
                                        setShowHelperOnBlur(isEmpty(selectedValue));
                                        validateOnBlur(selectedValue);
                                    },
                                }),
                            })}
                            <Popper
                                open={isOpen}
                                anchorEl={anchorRef.current}
                                placement="bottom-start"
                                modifiers={[
                                    {
                                        name: "flip",
                                        enabled: true,
                                        options: {
                                            fallbackPlacements: ["top-start"],
                                        },
                                    },
                                    {
                                        name: "preventOverflow",
                                        enabled: true,
                                        options: {
                                            boundary: "clippingParents",
                                        },
                                    },
                                ]}
                                style={{ width: anchorRef.current?.clientWidth, zIndex: 1300 }}
                            >
                                <StyledPaper square {...getMenuProps({}, { suppressRefError: true })}>
                                    <MenuList>
                                        {suggestions &&
                                            suggestions.map((suggestion, index) =>
                                                renderSuggestion({
                                                    suggestion,
                                                    index,
                                                    itemProps: getItemProps({ item: suggestion }),
                                                    highlightedIndex,
                                                    selectedItem: !freeFormValue ? null : selectedItem,
                                                    key: suggestion[options.key],
                                                    display: options.display,
                                                    displaySub: options.displaySub,
                                                    displayType: options.displayType,
                                                })
                                            )}
                                    </MenuList>
                                </StyledPaper>
                            </Popper>
                        </StyledContainer>
                    );
                }}
            </Downshift>
        </div>
    );
};

export default Autocomplete;